import * as React from 'react'

import { Box, Grid, Text } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import PricingBox from 'modules/billing/components/PricingBox'
import { ChargebeeBundle, NormalPlan } from 'modules/billing/lib/prices'
import { BundleConfig, ValidBundleName } from 'modules/billing/pages/BundlesHomepage/types'
import { PricingBundlePlanStructure } from 'modules/onboarding/lib/pricing/types'

import PeriodSelectorConfigPlan from 'modules/billing/components/PeriodSelector/ConfigPlan'
import BundleSelector from './_BundleSelector'
import Footer from './_Footer'
import { mergeInheritFeatures } from './helper'
import type { ValidPlanType } from './types'

type Props = {
  bundlesStructure: PricingBundlePlanStructure
  configPlan: BundleConfig
  setConfigPlan: React.Dispatch<React.SetStateAction<BundleConfig>>
  hubsSectionRef: React.RefObject<HTMLElement>
}

const FactorialBundles = ({
  bundlesStructure,
  configPlan,
  setConfigPlan,
  hubsSectionRef
}: Props) => {
  const bundlesSectionRef = React.useRef<HTMLElement>(null)
  const isYearly = configPlan.period === 'yearly'
  const pricingBoxCopies = {
    business: {
      title: i18n.t('pricing_plans.hubs.business.title'),
      description: i18n.t('pricing_plans.hubs.business.description')
    },
    enterprise: {
      title: i18n.t('pricing_plans.hubs.enterprise.title'),
      description: i18n.t('pricing_plans.hubs.enterprise.description')
    }
  }
  const chargebeeBundle = bundlesStructure.packs.find((bundle) => {
    return bundle.business.title.toLowerCase().includes(configPlan.bundle.toLowerCase())
  })
  const getPricingConfig = (planType: ValidPlanType) => {
    const bundleConfig = mergeInheritFeatures(
      planType,
      chargebeeBundle?.[planType] as unknown as ChargebeeBundle
    )

    return { ...bundleConfig, ...pricingBoxCopies[planType] }
  }
  const businessPricingConfig = getPricingConfig('business')
  const enterprisePricingConfig = getPricingConfig('enterprise')

  const MAX_FEATURES_PER_BUNDLE = 8
  const getFeatures = (plan: NormalPlan, excludingFeatures: string[]) => {
    const inherited = !!plan.inheritFeatures
      ? plan.inheritFeatures.regions.features.map((e) => e.title).concat(excludingFeatures)
      : excludingFeatures

    if (!inherited.length) return plan.regions.features.map((f) => f.title)

    return plan.regions.features.filter((f) => !inherited.includes(f.title)).map((f) => f.title)
  }
  const getBenefits = (config: ChargebeeBundle, excludingFeatures: string[]) =>
    config.normalPlans
      .map((plan) => getFeatures(plan, excludingFeatures))
      .flat()
      .slice(0, MAX_FEATURES_PER_BUNDLE)

  const businessBenefits = getBenefits(businessPricingConfig, [])
  const enterpriseBenefits = getBenefits(enterprisePricingConfig, businessBenefits)

  return (
    <Box width="full" alignSelf="center" style={{ maxWidth: '1120px' }} gap="s96">
      <Box gridColSpan={{ xxs: 4 }} gap="s24">
        <Text size="400" color="grey1000" weight="semibold" innerRef={hubsSectionRef}>
          {i18n.t('pricing_plans.hubs.first_step')}
        </Text>
        <BundleSelector
          selectBundle={(bundle: string) => {
            setConfigPlan({ ...configPlan, bundle: bundle as ValidBundleName })
          }}
          selectedBundle={configPlan.bundle}
          bundlesStructure={bundlesStructure}
          scrollableSectionRef={bundlesSectionRef}
        />
      </Box>
      <Box gridColSpan={{ xxs: 8 }} gap="s24">
        <Box flexDirection="row" gap="s32" width="full">
          <Text size="400" color="grey1000" weight="semibold" innerRef={bundlesSectionRef}>
            {i18n.t('pricing_plans.hubs.second_step', { bundle: configPlan.bundle })}
          </Text>
          <PeriodSelectorConfigPlan configPlan={configPlan} setConfigPlan={setConfigPlan} />
        </Box>
        <Grid variant="fixed">
          <Box gridColSpan={{ xxs: 12, s: 9 }}>
            <Grid variant="fixed">
              <Box gridColSpan={{ xxs: 6 }}>
                <PricingBox
                  planType="business"
                  isYearly={isYearly}
                  config={businessPricingConfig}
                  benefits={businessBenefits}
                  selectedBundle={configPlan.bundle}
                />
              </Box>
              <Box gridColSpan={{ xxs: 6 }}>
                <PricingBox
                  planType="enterprise"
                  isYearly={isYearly}
                  config={enterprisePricingConfig}
                  benefits={enterpriseBenefits}
                  selectedBundle={configPlan.bundle}
                />
              </Box>
            </Grid>
          </Box>
        </Grid>
      </Box>
      <Footer />
    </Box>
  )
}

export default FactorialBundles
